import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Modal, Row } from "react-bootstrap";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { RootState } from "../../../../redux/store";
import { setField, setProposedActionsPage } from "../../../../redux/improvementPlanSlice";
import { ImprovementPlanActionFormDto, ImprovementPlanFormDto } from "../../types";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { formatDate } from "../../../../utils/formatters/DateFormatter";
import ImprovementPlanActionForm from "../ImprovementPlanActionForm";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { getImprovementPlanActionFormSchema } from "../../schema/improvementPlanActionSchema";
import { ActionClaimTypeEnum, ActionStatusEnum } from "../../types/enums";
import { convertUtc } from "../../../../utils/dateUtils";

type ImprovementPlanActionsTabProps = {
    responsibleOptions?: SelectItemDto[];
};

type ActionRow = ImprovementPlanActionFormDto & {
    rowIndex: number;
};

const getEmptyActionForm = (): ImprovementPlanActionFormDto => ({
    id: 0,
    description: "",
    claimType: undefined,
    status: undefined,
    responsibleId: undefined,
    actionTypeId: undefined,
    startDate: undefined,
    endDate: undefined,
    comments: [],
    files: []
});

export default function ImprovementPlanActionsTab({
    responsibleOptions = []
}: ImprovementPlanActionsTabProps) {
    const { t } = useTranslation(["improvementPlan", "common"]);
    const dispatch = useDispatch();
    const actions = useSelector((state: RootState) => state.improvementPlanForm.data.actions) ?? [];

    const [showModal, setShowModal] = useState(false);
    const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
    const [actionForm, setActionForm] = useState<ImprovementPlanActionFormDto>(getEmptyActionForm());
    const [actionErrors, setActionErrors] = useState<{ [key: string]: string | undefined }>({});

    const rows: ActionRow[] = useMemo(() => actions.map((action, index) => ({
        ...action,
        rowIndex: index,
    })), [actions]);

    const columns: ColumnConfig<ActionRow>[] = [
        { header: t("common:form.fields.id"), key: "id", render: (item) => <b>{item.id}</b> },
        {
            header: t("common:form.fields.description"),
            key: "description",
            headerClassName: "text-align-left",
            cellStyle: { textAlign: "left", minWidth: "480px" }
        },
        {
            header: t("common:form.fields.status"),
            key: "status",
            render: (item) => <>{item.status !== undefined ? t(`improvementPlan:actionForm.status.${ActionStatusEnum[item.status]}`) : "-"}</>
        },
        {
            header: t("improvementPlan:actionsTable.claimType"),
            key: "claimType",
            render: (item) => <>{item.claimType !== undefined ? t(`improvementPlan:actionForm.claimType.${ActionClaimTypeEnum[item.claimType]}`) : "-"}</>
        },
        {
            header: t("common:form.fields.responsible"),
            key: "responsibleName",
            render: (item) => <>{item.responsibleName ?? (item.responsibleId ? responsibleOptions.find((x) => x.id === item.responsibleId)?.name : "-") ?? "-"}</>
        },
        { header: t("common:form.fields.startDate"), key: "startDate", render: (item) => <>{formatDate(item.startDate)}</> },
        { header: t("common:form.fields.endDate"), key: "endDate", render: (item) => <>{formatDate(item.endDate)}</> },
    ];

    const handleOpenCreateModal = () => {
        setEditingRowIndex(null);
        setActionForm(getEmptyActionForm());
        setActionErrors({});
        setShowModal(true);
    };

    const handleOpenEditModal = (row: ActionRow) => {
        const { rowIndex, ...action } = row;

        setEditingRowIndex(rowIndex);
        setActionForm(action);
        setActionErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setActionErrors({});
        setShowModal(false);
    };

    const updateActionField = <FormField extends keyof ImprovementPlanActionFormDto>(
        field: FormField,
        value: ImprovementPlanActionFormDto[FormField]
    ) => {
        if (field === "startDate" || field === "endDate" || field === "expectedEndDate") {
            value = convertUtc(value);
        }
        setActionForm((prev) => ({ ...prev, [field]: value }));
        setActionErrors((prev) => {
            const next = { ...prev, [field]: undefined };
            if (field === "comments") {
                Object.keys(next).forEach((key) => {
                    if (key.startsWith("comments[")) {
                        next[key] = undefined;
                    }
                });
            }
            return next;
        });
    };

    const handleSaveAction = async () => {
        const existingTitles = actions
            .filter((_, index) => index !== editingRowIndex)
            .map((action) => action.description);

        const schema = getImprovementPlanActionFormSchema(t, existingTitles);
        setActionErrors({});

        try {
            await schema.validate(actionForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            if (err?.inner && Array.isArray(err.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        validationErrors[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                validationErrors[err.path] = err.message;
            }
            setActionErrors(validationErrors);
            return;
        }

        const updatedActions = [...actions];
        if (editingRowIndex === null) {
            updatedActions.push(actionForm);
        } else {
            updatedActions[editingRowIndex] = actionForm;
        }

        dispatch(setField({ field: "actions", value: updatedActions as ImprovementPlanFormDto["actions"] }));
        handleCloseModal();
    };

    const handleDeleteAction = (actionToDelete: ActionRow) => {
        const updatedActions = actions.filter((_, index) => index !== actionToDelete.rowIndex);
        dispatch(setField({ field: "actions", value: updatedActions as ImprovementPlanFormDto["actions"] }));
    };

    return (
        <>
            <Row className="mb-2 d-flex align-items-center justify-content-between">
                <Col xs="auto">
                    <h5 className="mb-0">{t("improvementPlan:tabs.actions")}</h5>
                </Col>
                <Col xs="auto">
                    <Button variant="secondary" type="button" onClick={() => dispatch(setProposedActionsPage({ proposedActionsPage: true }))}>
                        {t("improvementPlan:proposeActions.button")}
                    </Button>
                    <Button variant="secondary" type="button" className="ms-2" onClick={handleOpenCreateModal}>
                        {t("improvementPlan:actionsButtonNew")}
                    </Button>
                </Col>
            </Row>

            <AppTable<ActionRow>
                data={rows}
                columns={columns}
                emptyMessage={t("improvementPlan:actionsEmptyMessage")}
                t={t}
                hasActions={true}
                hasPagination={false}
                onDelete={handleDeleteAction}
                onEditItem={handleOpenEditModal}
                sectionPermission={SectionPermissionTypeEnum.IMPROVEMENT_PLANS}
            />

            <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
                <Modal.Header closeButton></Modal.Header>
                <Modal.Body>
                    <ImprovementPlanActionForm
                        actionForm={actionForm}
                        errors={actionErrors}
                        updateField={updateActionField}
                        responsibleOptions={responsibleOptions}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        {t("common:buttons.buttonCancel")}
                    </Button>
                    <Button variant="primary" onClick={handleSaveAction}>
                        {t("common:save")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
